import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ChatWidget.css';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api/').replace(/\/api\/?$/, '');

const getQuickStarts = () => {
  const month = new Date().getMonth(); // 0 = Jan, 9 = Oct (Diwali season)
  const seasonal = month === 9 || month === 10
    ? { label: '🪔 Diwali gifting', message: "I'm looking for Diwali gift ideas for my employees" }
    : month === 11 || month === 0
    ? { label: '🎉 New Year gifting', message: "I need New Year corporate gift ideas for our clients" }
    : month === 1 || month === 2
    ? { label: '💼 New FY gifting', message: "We want to plan corporate gifts for the new financial year" }
    : { label: '🏆 Employee recognition', message: "I'm looking for employee recognition and appreciation gifts" };

  return [
    seasonal,
    { label: '👕 Branded merchandise', message: "I need branded merchandise with our company logo" },
    { label: '📦 Bulk order quote', message: "I want to place a bulk order, can I get a quote?" },
    { label: '📊 Check quote status', message: "I want to check the status of my existing quote" },
  ];
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [rfqSubmitted, setRfqSubmitted] = useState(false);
  const [teaserVisible, setTeaserVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const teaserTimerRef = useRef(null);

  // Show teaser bubble after 4s, auto-dismiss after 10s — once per session
  useEffect(() => {
    if (sessionStorage.getItem('yantra-teaser-shown')) return;
    const showTimer = setTimeout(() => {
      setTeaserVisible(true);
      sessionStorage.setItem('yantra-teaser-shown', '1');
      teaserTimerRef.current = setTimeout(() => setTeaserVisible(false), 10000);
    }, 4000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(teaserTimerRef.current);
    };
  }, []);

  const openChat = () => {
    setTeaserVisible(false);
    setIsOpen(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Greeting on first open
      setMessages([{
        role: 'model',
        content: "👋 Hi! I'm Yantra, eYantra's assistant for corporate gifting.\n\nHow can I help you today?",
      }]);
    }
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    const userText = text.trim();
    if (!userText || isStreaming) return;

    const newMessages = [...messages, { role: 'user', content: userText }];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);

    // Placeholder for the streaming assistant reply
    setMessages(prev => [...prev, { role: 'model', content: '', streaming: true }]);

    try {
      abortRef.current = new AbortController();
      const timeoutId = setTimeout(() => abortRef.current.abort(), 25000);
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
        signal: abortRef.current.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error('Chat request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        // SSE format: each line is "data: <text>\n\n"
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data:')) {
            accumulated += line.slice(5);
          }
        }

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'model', content: accumulated, streaming: true };
          return updated;
        });
      }

      // Finalize — check if AI returned a structured action
      const finalContent = accumulated;
      const actionMatch = finalContent.match(/\{[\s\S]*?"action"\s*:\s*"[^"]+?"[\s\S]*?\}/);

      if (actionMatch) {
        try {
          const actionData = JSON.parse(actionMatch[0]);

          if (actionData.action === 'submit_rfq') {
            try {
              await submitRfqFromChat(actionData, newMessages);
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'model',
                  content: `✅ Your quote request has been submitted!\n\nWe'll send a detailed quote to *${actionData.email}* within 4 business hours. Reference ID will be in your email. 🎁`,
                  streaming: false,
                };
                return updated;
              });
              setRfqSubmitted(true);
            } catch {
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'model',
                  content: `❌ Something went wrong submitting your request. Please email us directly at *quotes@eyantra.com* with your details and we'll get back to you within 4 business hours.`,
                  streaming: false,
                };
                return updated;
              });
            }

          } else if (actionData.action === 'check_status') {
            const result = await checkQuoteStatus(actionData.reference, actionData.email);
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'model',
                content: result.found
                  ? `📋 *Ref: ${actionData.reference}*\n\n${result.message}`
                  : `❌ ${result.message}`,
                streaming: false,
              };
              return updated;
            });
          }
        } catch (e) {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'model', content: finalContent, streaming: false };
            return updated;
          });
        }
      } else {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'model', content: finalContent, streaming: false };
          return updated;
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'model',
            content: "Sorry, I ran into a problem. Please email us at quotes@eyantra.com or try again.",
            streaming: false,
          };
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
    }
  }, [messages, isStreaming]);

  const checkQuoteStatus = async (reference, email) => {
    try {
      const res = await fetch(`${API_BASE}/api/quotes/status?ref=${encodeURIComponent(reference)}&email=${encodeURIComponent(email)}`);
      return await res.json();
    } catch {
      return { found: false, message: "Couldn't reach our servers. Please try again or email quotes@eyantra.com" };
    }
  };

  const submitRfqFromChat = async (rfqData, conversationMessages) => {
    const transcript = (conversationMessages || [])
      .filter(m => m.content && m.content.trim())
      .map(m => `[${m.role === 'user' ? 'Customer' : 'Yantra'}]: ${m.content.trim()}`)
      .join('\n');

    const notes = [
      rfqData.notes || 'Enquiry submitted via AI chat',
      '',
      '--- Conversation Transcript ---',
      transcript,
    ].join('\n');

    const res = await fetch(`${API_BASE}/api/quotes/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: rfqData.name,
        email: rfqData.email,
        phone: rfqData.phone,
        companyName: rfqData.company,
        notes,
        items: [],
      }),
    });
    if (!res.ok) throw new Error(`RFQ submission failed: ${res.status}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const resetChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setRfqSubmitted(false);
    setIsStreaming(false);
  };

  const escapeHtml = (str) =>
    str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
       .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const formatContent = (content) => {
    return escapeHtml(content)
      // Ensure numbered list items always start on a new line
      .replace(/([^\n])(\s*)(\d+\.\s)/g, '$1\n$3')
      // Bold markdown (applied after escaping so we don't escape our own tags)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      // Newlines to <br>, then style lines that open a numbered item
      .replace(/\n/g, '<br/>')
      .replace(/<br\/>((\d+)\.\s)/g, '<br/><span class="chat-li">$1</span>');
  };

  return (
    <div className="chat-widget">
      {/* Teaser bubble */}
      {teaserVisible && !isOpen && (
        <div className="chat-teaser" onClick={openChat}>
          <button
            className="chat-teaser-close"
            onClick={e => { e.stopPropagation(); setTeaserVisible(false); }}
            aria-label="Dismiss"
          >×</button>
          <p>👋 Need help with corporate gifting?</p>
          <span>Ask Yantra — get a quote in minutes!</span>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        className={`chat-trigger ${isOpen ? 'open' : ''} ${teaserVisible && !isOpen ? 'pulsing' : ''}`}
        onClick={() => { if (isOpen) setIsOpen(false); else openChat(); }}
        aria-label="Chat with us"
      >
        {isOpen ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
        )}
        {!isOpen && <span className="chat-trigger-label">Ask Yantra!</span>}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="chat-window">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">Y</div>
              <div>
                <div className="chat-header-name">Yantra</div>
                <div className="chat-header-sub">eYantra Corporate Gifting</div>
              </div>
            </div>
            <button className="chat-reset" onClick={resetChat} title="Start new conversation">↺</button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble-row ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                {msg.role === 'model' && <div className="chat-bubble-avatar">Y</div>}
                <div className={`chat-bubble ${msg.role === 'user' ? 'user' : 'assistant'} ${msg.streaming ? 'streaming' : ''}`}>
                  <span dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }} />
                  {msg.streaming && msg.content === '' && (
                    <span className="typing-dots"><span/><span/><span/></span>
                  )}
                </div>
              </div>
            ))}

            {/* Quick start buttons — show only at start */}
            {messages.length === 1 && !isStreaming && (
              <div className="quick-starts">
                {getQuickStarts().map((qs, i) => (
                  <button key={i} className="quick-start-btn" onClick={() => sendMessage(qs.message)}>
                    {qs.label}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="chat-input-area">
            {rfqSubmitted ? (
              <div className="chat-submitted-note">
                Quote submitted! We'll be in touch within 4 hours.
                <button onClick={resetChat}>Start new chat</button>
              </div>
            ) : (
              <>
                <textarea
                  ref={inputRef}
                  className="chat-input"
                  placeholder="Type a message... (English, Hindi, Telugu)"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isStreaming}
                />
                <button
                  className="chat-send"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || isStreaming}
                  aria-label="Send"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
